<template>
  <div
    class="form__group"
    :class="{
      error: !!errorMessage,
      '-has-label': showLabel && label,
    }"
  >
    <label v-if="showLabel" :for="labelFor || inputId">
      {{ label }}
      <slot name="label" />
    </label>
    <slot
      :handle-change="handleChange"
      :handle-blur="handleBlur"
      :value="value"
      :error-message="errorMessage"
    />
    <input-error v-if="errorMessage && showErrorMsg" :error-message="errorMessage" />
    <slot name="note" />
  </div>
</template>

<script setup lang="ts">
/**
 * Slotted input that is passed in. Do not use v-model, just pass in value
 * and it will automatically be synced to vee-validate.
 */
import { computed, watch } from 'vue'
import { useField } from 'vee-validate'
import InputError from '@/components/forms/input-error.vue'

/** OPTIONS **/
defineOptions({
  // allow $attrs to flow through to the input element
  inheritAttrs: false,
})

/** PROPS **/
const props = withDefaults(
  defineProps<{
    // not using v-model
    value: any
    inputId: string
    label?: string
    labelFor?: string // if inputId is not the actual input
    showErrorMsg?: boolean
    showLabel?: boolean
    rules?: string
    // validation label
    validateAs?: string
  }>(),
  {
    label: '',
    validateAs: '',
    labelFor: '',
    showLabel: true,
    showErrorMsg: true,
    rules: '',
  },
)

/** COMPUTED **/
// couldn't pass props directly in, needed to be reactive
const reactiveRules = computed(() => props.rules)

/** WATCHERS **/
// sync prop value to field value
watch(
  () => props.value,
  (newValue: any, oldValue: any) => {
    if (newValue !== oldValue) {
      handleChange(newValue, true)
    }
  },
)

/** STATE **/
const { value, errorMessage, handleBlur, handleChange } = useField(
  () => props.inputId,
  reactiveRules,
  {
    initialValue: props.value,
    label: props.validateAs || props.label || 'Field',
    validateOnValueUpdate: false,
    validateOnMount: false,
  },
)

/** METHODS **/
</script>
